import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ViewerAsset } from '@models/viewer-asset';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Options } from '@popperjs/core';
import { ButtonComponent } from '@shared/button/button.component';
import { MarkdownService } from '@shared/markdown.service';
import { SelectComponent } from '@shared/select/select.component';
import { ControlPopoverComponent } from '@shared/viewer/viewer-controls/control-popover/control-popover.component';
import { ZbpIconComponent } from '@shared/zbp-icon/zbp-icon';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';

import { Subscription } from 'rxjs';
import { AudioPlayerComponent } from '../../audio-player/audio-player.component';
import { ViewerService } from '../viewer.service';

@Component({
  selector: 'zbp-viewer-audio-player',
  templateUrl: './viewer-audio-player.component.html',
  styleUrls: ['./viewer-audio-player.component.scss'],
  imports: [
    CommonModule, ButtonComponent, AudioPlayerComponent, ControlPopoverComponent, NgbPopover,
    SelectComponent, ReactiveFormsModule, ZbpIconComponent]
})
export class ViewerAudioPlayerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() assetIds: string[] = [];
  @Input() assetList: ViewerAsset[] = [];
  @Input() isDesktop: boolean;

  /** a filtered list of assets based on {@link assetIds} and {@link assetList} */
  assets: ViewerAsset[] = [];

  /** used for calculating if {@link assets} has actually changed */
  previousAssets: ViewerAsset[] = [];

  assetName: string = '';
  sameSetOfAudioIds: boolean = false;

  /** New property for text alternative */
  transcript: string = '';

  /** Toggle for expanded visibility */
  showTranscript: boolean = false;

  isPopoverOpen: boolean = false;

  readonly assetControl = new FormControl<ViewerAsset>(null);

  audioFile: string = null;

  private readonly subscriptions = new Subscription();
  protected readonly zbpIconNames = zbpIconNames;

  @ViewChild('popoverControl') popoverControl!: ControlPopoverComponent;

  constructor(
    private markdown: MarkdownService,
    private viewerService: ViewerService,
    public cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.assetControl.valueChanges.subscribe(this.onAssetSelection.bind(this))
    );
    this.setAssets();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.assetIds) {
      this.setAssets();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onAssetSelection(asset: ViewerAsset): void {
    this.updateAudio(asset);
    this.updateTranscript(asset);
    this.cdr.detectChanges();
  }

  private updateAudio(asset: ViewerAsset): void {
    this.audioFile = asset
      ? this.viewerService.getAssetFilenameUrl(asset, 'media', asset.language)
      : null;
  }

  private updateTranscript(asset: ViewerAsset): void {
    if (asset) {
      this.transcript = asset.transcript
        ? this.markdown.render(asset.transcript)
        : asset.name;
    } else {
      this.transcript = null;
    }
  }

  setAssets(): void {
    const filteredAssets = this.assetList?.filter(
      asset => asset.assetId !== '' && this.assetIds.includes(asset.assetId)
    ) ?? [];

    if (filteredAssets.length > 0) {
      this.assets = filteredAssets;
      this.sameSetOfAudioIds = this.previousAssets != null
        && this.previousAssets.length > 0
        && this.previousAssets.toString() === filteredAssets.toString();
      this.previousAssets = filteredAssets;
      const assetToSelect = this.sameSetOfAudioIds
        ? this.assetControl.value
        : this.assets[0];
      this.assetControl.setValue(assetToSelect, { emitEvent: true });
    } else {
      this.assets = [];
      this.previousAssets = [];
      this.assetControl.setValue(null);
    }
  }

  toggleTranscript(): void {
    this.showTranscript = !this.showTranscript;
  }

  showPopover() {
    this.isPopoverOpen = true;
    this.popoverControl.onPopoverShown();
  }

  closePopover() {
    this.isPopoverOpen = false;
  }

  // Modifying Popper.js options
  popperOptions(options: Options): Options {
    return {
      ...options,
      strategy: 'absolute',
      modifiers: [
        ...(options.modifiers || []),
        {
          name: 'offset',
          options: {
            offset: [0, 0],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            // Allows popover to stay visible even when scrolled
            padding: 0,
          },
        },
      ],
    };
  }

}
